import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText, {
  Opts as ButtonScaleViewWithTextOpts,
} from 'src/lib/ui/components/ButtonScaleViewWithText';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { VisitDetailsData } from 'src/replicant/visit';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  ImageSliceView,
  ImageSliceViewOpts,
  ImageView,
} from '@play-co/timestep-core/ui';
import ImageScaleView, {
  ImageScaleViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageScaleView';
import popups from '../../../lib/ui/config/popups';
import { getTimestampText } from '../visit.util';
import { ImageViewOpts } from '@play-co/timestep-core/lib/ui/ImageView';
import { HudMeter, HudMeterOpts } from '../../components/HudMeter';
import { Pet } from 'src/replicant/buruburu/pet';
import { Avatar, AvatarOpts } from '../../components/shared/Avatar';
import { getCreativeSuffix } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';

namespace style {
  export const popup = {
    zIndex: 10000,
    width: uiConfig.popups.buruburu.width,
    height: 757,
    darkerBg: false,
  };

  export const message: LangBitmapOpts = {
    size: 30,
    height: 64,
    x: 52,
    y: 89,
    zIndex: 1,
  };
  message.width = popup.width - 2 * message.x;

  export const underline: ViewOpts = {
    width: message.width,
    y: message.height,
    height: 4,
    backgroundColor: '#FDC381',
  };

  export const cancelButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...uiConfig.buttons.cancel,
    labelOffsetY: -1,
    fontSize: 46,
    font: 'Body',
    width: 230,
    height: 100,
    centerOnOrigin: true,
  };
  cancelButton.x = (popup.width - cancelButton.width) / 2 - 10;
  cancelButton.y = popup.height - 25 - cancelButton.height / 2;

  export const okButton: Omit<ButtonScaleViewWithTextOpts, 'tracking'> = {
    ...cancelButton,
    ...uiConfig.buttons.secondary,
  };
  okButton.x = (popup.width + okButton.width) / 2 + 10;

  export namespace player {
    export const view: ImageSliceViewOpts = {
      x: 25,
      y: message.y + message.height + 25,
      height: 180,
      image: 'assets/ui/shared/lossless/scroller_panel_blue.png',
      slices: {
        top: 19,
        left: 19,
        right: 22,
        bot: 28,
      },
    };
    view.width = popups.buruburu.width - 2 * view.x;

    export const image: AvatarOpts = {
      centerOnOrigin: false,
      width: 128,
      height: 128,
      x: 16,
      y: 25,
    };

    export const name: LangBitmapOpts = {
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
      height: 48,
      x: image.x + image.width + 10,
      y: image.y + 3,
    };
    name.width = view.width - name.x - 20;

    export const visits: LangBitmapOpts = {
      font: name.font,
      color: name.color,
      x: name.x,
      y: name.y + name.height,
      height: 36,
      size: 22,
      align: 'left',
      verticalAlign: 'center',
    };
    visits.width = (view.width - visits.x) / 2 - 15;

    export const visited: LangBitmapOpts = {
      ...visits,
    };
    visited.y += visits.height;

    export const lastLogin: LangBitmapOpts = {
      ...visits,
      align: 'right',
      wordWrap: true,
    };
    const margin = 10;
    lastLogin.width -= margin;
    lastLogin.x += visits.width + margin;
    lastLogin.height *= 2;
  }

  export namespace pet {
    export const view: ViewOpts = {
      x: 25,
      y: player.view.y + player.view.height + 10,
      height: 180,
    };
    view.width = popups.buruburu.width - 2 * view.x;

    export const speciesIconBackdrop: ImageViewOpts = {
      image: 'assets/ui/shared/lossless/backdrop_panel.png',
      x: player.image.x,
      y: player.image.y,
      width: player.image.width,
      height: player.image.height,
    };

    export const speciesIcon: ImageScaleViewOpts = {
      x: speciesIconBackdrop.width / 2,
      y: speciesIconBackdrop.height / 2,
      width: speciesIconBackdrop.width - 10,
      height: speciesIconBackdrop.height - 10,
      scaleMethod: 'contain',
      centerOnOrigin: true,
    };

    export const name: LangBitmapOpts = {
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
      width: 130,
      height: 48,
      x: speciesIconBackdrop.x + speciesIconBackdrop.width + 10,
      y: speciesIconBackdrop.y + 3,
    };

    export const species: LangBitmapOpts = {
      font: name.font,
      color: name.color,
      x: name.x,
      y: name.y + name.height,
      width: name.width,
      height: 36,
      size: 22,
      align: 'left',
      verticalAlign: 'center',
    };

    export const age: LangBitmapOpts = {
      ...species,
    };
    age.y += age.height;

    export const stomach: HudMeterOpts = {
      ...uiConfig.hud.stomachMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    stomach.view = {
      ...stomach.view,
      x: name.x + name.width + 10,
      y: 20,
    };

    export const love: HudMeterOpts = {
      ...uiConfig.hud.loveMeter,
      label: uiConfig.hud.meterLabel,
      text: uiConfig.hud.meterText,
    };
    love.view = {
      ...love.view,
      x: stomach.view.x + stomach.view.width,
      y: stomach.view.y,
    };
  }

  export const dashedLine: ImageViewOpts = {
    image: 'assets/ui/shared/lossless/dashed_line.png',
    width: 389,
    height: 10,
    x: popup.width / 2,
    y: pet.view.y + pet.view.height + 20,
    centerOnOrigin: true,
  };

  export const disclaimer: LangBitmapOpts = {
    y: dashedLine.y + dashedLine.height,
    height: 48,
    align: 'center',
    verticalAlign: 'center',
    color: pet.name.color,
    font: 'Body',
    size: 28,
  };
  disclaimer.width = popup.width;
}

type CloseHandler = (result: boolean) => void;

export default class PopupConfirmVisitFriend extends PopupBasic {
  private buttonCancel: ButtonScaleViewWithText;
  private buttonOk: ButtonScaleViewWithText;
  private close: CloseHandler;
  private playerPhoto: Avatar;
  private playerName: LangBitmapFontTextView;
  private visits: LangBitmapFontTextView;
  private visited: LangBitmapFontTextView;
  private lastLogin: LangBitmapFontTextView;
  private speciesIcon: ImageScaleView;
  private petName: LangBitmapFontTextView;
  private species: LangBitmapFontTextView;
  private age: LangBitmapFontTextView;
  private stomach: HudMeter;
  private love: HudMeter;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...style.popup,
      superview: opts.superview,
      close: () => opts.close(false),
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.POPUP_CONFIRM_VISIT._,
      },
      titleIcon: { image: 'assets/ui/buruburu/visitfriend_icon.png' },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts(style.message);

    this.message.addSubview(new View(style.underline));

    this.box.addSubview(this.createPlayerView());

    this.box.addSubview(this.createPetView());

    this.box.addSubview(new ImageView(style.dashedLine));

    this.box.addSubview(
      new LangBitmapFontTextView({
        ...style.disclaimer,
        localeText: () => i18n('friendsVisit.confirm.disclaimer'),
      }),
    );

    this.buttonCancel = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.cancelButton,
        localeText: () => i18n('basic.cancel'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_CONFIRM_VISIT.CLOSE,
        },
      }),
    );

    this.buttonOk = this.box.addSubview(
      new ButtonScaleViewWithText({
        ...style.okButton,
        localeText: () => i18n('friendsVisit.confirm.ok'),
        onClick: async () => {
          sounds.sfx.play('ok');
          return opts.close(true);
        },
        tracking: {
          feature: Feature.VISIT._,
          $subFeature: Feature.VISIT.POPUP_CONFIRM_VISIT.OK,
        },
      }),
    );
  }

  init(opts: VisitDetailsData) {
    super.init({
      title: i18n('friendsVisit.confirm.title'),
      message: i18n('friendsVisit.confirm.message'),
    });

    this.playerPhoto.updateImage(
      opts.photo || 'assets/ui/shared/icons/icon_avatar_generic.png',
    );
    this.playerName.localeText = () => opts.name;
    this.visits.localeText = () => i18n('friendsVisit.confirm.visits', opts);
    this.visited.localeText = () => i18n('friendsVisit.confirm.visited', opts);
    this.lastLogin.localeText = () =>
      `${i18n('friendsVisit.popup.lastLogin')}\n${getTimestampText(
        opts.lastLogin,
      )}`;

    this.speciesIcon.setImage(`assets/species/${opts.species}.png`);
    this.petName.localeText = () => opts.petName;
    this.species.localeText = () =>
      Pet.ruleset.evolutions[opts.species]?.[`name_${getCreativeSuffix()}`];
    this.age.localeText = () => i18n('friendsVisit.confirm.age', opts);
    const { max } = Pet.ruleset.attributeBounds;
    this.stomach.setMeterValue(opts.stomach / max);
    this.love.setMeterValue(opts.love / max);
  }

  private createPlayerView() {
    const view = new ImageSliceView(style.player.view);

    this.playerPhoto = new Avatar({
      ...style.player.image,
      superview: view,
    });

    this.playerName = view.addSubview(
      new LangBitmapFontTextView(style.player.name),
    );

    this.visits = view.addSubview(
      new LangBitmapFontTextView(style.player.visits),
    );

    this.visited = view.addSubview(
      new LangBitmapFontTextView(style.player.visited),
    );

    this.lastLogin = view.addSubview(
      new LangBitmapFontTextView(style.player.lastLogin),
    );

    return view;
  }

  private createPetView() {
    const view = new ImageSliceView(style.pet.view);

    const speciesIconBackdrop = view.addSubview(
      new ImageView(style.pet.speciesIconBackdrop),
    );
    this.speciesIcon = speciesIconBackdrop.addSubview(
      new ImageScaleView({
        ...style.pet.speciesIcon,
      }),
    );

    this.petName = view.addSubview(new LangBitmapFontTextView(style.pet.name));

    this.species = view.addSubview(
      new LangBitmapFontTextView(style.pet.species),
    );

    this.age = view.addSubview(new LangBitmapFontTextView(style.pet.age));

    this.stomach = view.addSubview(new HudMeter(style.pet.stomach));

    this.love = view.addSubview(new HudMeter(style.pet.love));

    return view;
  }
}
